import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { enqueueSnackbar } from "notistack";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import {
  addSite,
  agencyOnboardingBrandsQueryOptions,
  agencyOnboardingKeys,
  type AgencyOnboardingListBrand,
} from "@/api/agencyOnboarding";
import { useSites } from "@/api/zero/sites";
import { createAgencyOnboardingChecklist } from "@/features/BestiesOnboarding/defaultAgencyOnboardingChecklist";
import {
  hasElevatedAccessValue,
  useHasElevatedAccess,
  usePrivilegedModeStore,
} from "@/utils/privilegedMode";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

export const Route = createFileRoute(
  "/_auth/settings/$siteId/seed-besties-sites",
)({
  beforeLoad: ({ context }) => {
    const disabled = usePrivilegedModeStore.getState().disabled;
    if (!hasElevatedAccessValue(context.auth?.user?.user.role, disabled)) {
      throw redirect({ to: ".." });
    }
  },
  component: SeedSitesPage,
});

type RowStatus =
  | { kind: "idle" }
  | { kind: "pending" }
  | { kind: "success" }
  | { kind: "error"; message: string };

interface Row {
  id: string;
  name: string;
  url: string;
  isSeeded: boolean;
  selected: boolean;
  status: RowStatus;
}

const urlSchema = z.string().url();

const ROW_HEIGHT = 76;

function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return "Unknown error";
  }
}

function SeedSitesPage() {
  const navigate = useNavigate();
  const { siteId } = useParams({ strict: false });
  const hasElevatedAccess = useHasElevatedAccess();

  // beforeLoad only runs on navigation; boot reactively when privileged mode
  // toggles off while already on this page.
  useEffect(() => {
    if (!hasElevatedAccess) {
      void navigate({
        to: "/settings/$siteId",
        params: { siteId: siteId ?? "" },
      });
    }
  }, [hasElevatedAccess, navigate, siteId]);

  // Non-Connect sites never appear in /agency-onboarding/brands, so without
  // the Connect-only scope they would always show as "Missing" and be seedable.
  const sitesQuery = useSites({ isConnectOnly: true, accountState: "active" });
  const queryClient = useQueryClient();

  const brandsQuery = useQuery(agencyOnboardingBrandsQueryOptions());

  const brandsBySiteId = useMemo<Map<string, AgencyOnboardingListBrand>>(() => {
    const map = new Map<string, AgencyOnboardingListBrand>();
    for (const brand of brandsQuery.data?.brands ?? []) {
      map.set(brand.rj_site_id, brand);
    }
    return map;
  }, [brandsQuery.data]);

  const [rows, setRows] = useState<Row[] | null>(null);
  const [searchText, setSearchText] = useState("");

  const initialRows = useMemo<Row[]>(() => {
    return (sitesQuery.data ?? []).map((site) => ({
      id: site.rj_id,
      name: site.brand_name,
      url: normalizeUrl(site.url),
      isSeeded: brandsBySiteId.has(site.rj_id),
      selected: false,
      status: { kind: "idle" as const },
    }));
  }, [sitesQuery.data, brandsBySiteId]);

  const effectiveRows = rows ?? initialRows;

  const visibleRows = useMemo<Row[]>(() => {
    const q = searchText.trim().toLowerCase();
    if (q.length === 0) return effectiveRows;
    return effectiveRows.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        row.url.toLowerCase().includes(q) ||
        row.id.toLowerCase().includes(q),
    );
  }, [effectiveRows, searchText]);

  const updateRow = useCallback(
    (id: string, patch: Partial<Row>) => {
      setRows((prev) => {
        const base = prev ?? initialRows;
        return base.map((row) => (row.id === id ? { ...row, ...patch } : row));
      });
    },
    [initialRows],
  );

  const updateAllRows = useCallback(
    (updater: (row: Row) => Row) => {
      setRows((prev) => (prev ?? initialRows).map(updater));
    },
    [initialRows],
  );

  const missingCount = visibleRows.filter((r) => !r.isSeeded).length;
  const selectedRows = effectiveRows.filter((r) => r.selected && !r.isSeeded);
  const selectedVisibleCount = visibleRows.filter(
    (r) => r.selected && !r.isSeeded,
  ).length;
  const selectedHiddenCount = selectedRows.length - selectedVisibleCount;
  const completedCount = effectiveRows.filter(
    (r) => r.status.kind === "success",
  ).length;
  const errorCount = effectiveRows.filter(
    (r) => r.status.kind === "error",
  ).length;

  const handleSelectAllMissing = () => {
    const visibleIds = new Set(visibleRows.map((row) => row.id));
    updateAllRows((row) =>
      visibleIds.has(row.id) && !row.isSeeded
        ? { ...row, selected: true }
        : row,
    );
  };

  const handleClearSelection = () => {
    updateAllRows((row) => ({ ...row, selected: false }));
  };

  const handleResetStatuses = () => {
    updateAllRows((row) => ({ ...row, status: { kind: "idle" } }));
  };

  const [isRunning, setIsRunning] = useState(false);

  const runSeed = async () => {
    if (isRunning || selectedRows.length === 0) return;

    const invalid = selectedRows.filter(
      (r) => !urlSchema.safeParse(r.url).success,
    );
    if (invalid.length > 0) {
      enqueueSnackbar(
        `${invalid.length} selected row${
          invalid.length === 1 ? " has" : "s have"
        } an invalid URL. Fix or deselect them before seeding.`,
        { variant: "warning" },
      );
      return;
    }

    setIsRunning(true);
    let successes = 0;
    let failures = 0;

    for (const row of selectedRows) {
      updateRow(row.id, { status: { kind: "pending" } });
      try {
        await addSite(row.id, {
          brand_name: row.name || "Unnamed brand",
          site_name: row.name || "Unnamed site",
          website_url: row.url,
          onboarding_data: createAgencyOnboardingChecklist(),
        });
        updateRow(row.id, {
          status: { kind: "success" },
          isSeeded: true,
          selected: false,
        });
        successes += 1;
      } catch (err) {
        updateRow(row.id, {
          status: { kind: "error", message: getErrorMessage(err) },
        });
        failures += 1;
      }
    }

    await queryClient.invalidateQueries({
      queryKey: agencyOnboardingKeys.brands(),
    });

    setIsRunning(false);

    if (failures === 0 && successes > 0) {
      enqueueSnackbar(
        `Seeded ${successes} site${successes === 1 ? "" : "s"}.`,
        {
          variant: "success",
        },
      );
    } else if (failures > 0 && successes > 0) {
      enqueueSnackbar(
        `Seeded ${successes}, ${failures} failed. See rows for details.`,
        { variant: "warning" },
      );
    } else if (failures > 0) {
      enqueueErrorSnackbar(
        new Error(`All ${failures} seed attempts failed`),
        "Seeding failed",
      );
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? rowVirtualizer.getTotalSize() -
        virtualItems[virtualItems.length - 1].end
      : 0;

  if (!hasElevatedAccess) return null;

  return (
    <Box sx={{ p: 5, maxWidth: 1200, mx: "auto" }}>
      <Stack spacing={4}>
        <Box>
          <Typography variant="h4">Seed Besties sites in Connect</Typography>
          <Typography variant="body2" color="text.secondary">
            Internal admin tool. Loads the full site list from the Connect sites
            API (<code>RetrieveAllSites</code>) and registers entries that are
            missing from the onboarding API. Website URLs are pre-filled from
            each site record — edit them if needed before seeding.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            To remove this tool: delete{" "}
            <code>
              src/routes/_auth/settings/$siteId/seed-besties-sites.tsx
            </code>{" "}
            and its nav entry.
          </Typography>
        </Box>

        {sitesQuery.isPending ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading sites…</Typography>
          </Box>
        ) : null}

        {sitesQuery.error ? (
          <Alert severity="error">
            Could not load sites ({getErrorMessage(sitesQuery.error)}).
          </Alert>
        ) : null}

        {brandsQuery.isPending && brandsQuery.isFetching ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading Connect brands…</Typography>
          </Box>
        ) : null}

        {brandsQuery.error ? (
          <Alert severity="warning">
            Could not load Connect brands ({getErrorMessage(brandsQuery.error)}
            ). The seeded/missing flag may be inaccurate, but seeding will still
            work.
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={3}>
            <TextField
              size="small"
              placeholder="Search by brand name, URL, or site ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={isRunning}
              fullWidth
            />
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                variant="outlined"
                onClick={handleSelectAllMissing}
                disabled={isRunning || missingCount === 0}
              >
                Select all missing ({missingCount})
              </Button>
              <Button
                variant="text"
                onClick={handleClearSelection}
                disabled={isRunning}
              >
                Clear selection
              </Button>
              <Button
                variant="text"
                onClick={handleResetStatuses}
                disabled={isRunning}
              >
                Reset row statuses
              </Button>
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {completedCount} seeded · {errorCount} failed ·{" "}
                {selectedRows.length} selected
                {selectedHiddenCount > 0
                  ? ` (${selectedHiddenCount} hidden by filter)`
                  : ""}
              </Typography>
              <Button
                variant="contained"
                onClick={() => void runSeed()}
                disabled={isRunning || selectedRows.length === 0}
              >
                {isRunning
                  ? "Seeding…"
                  : `Seed selected (${selectedRows.length})`}
              </Button>
            </Stack>
            {isRunning ? <LinearProgress /> : null}
          </Stack>
        </Paper>

        <TableContainer
          component={Paper}
          variant="outlined"
          ref={scrollRef}
          sx={{ maxHeight: "65vh", overflow: "auto" }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }} />
                <TableCell>Site name</TableCell>
                <TableCell sx={{ width: 96 }}>Site ID</TableCell>
                <TableCell sx={{ width: "45%" }}>Website URL</TableCell>
                <TableCell sx={{ width: 112 }}>Seeded?</TableCell>
                <TableCell sx={{ width: 112 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      {effectiveRows.length === 0
                        ? "No sites returned from the sites API."
                        : "No sites match your search."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {paddingTop > 0 ? (
                <TableRow style={{ height: paddingTop }}>
                  <TableCell colSpan={6} sx={{ p: 0, border: 0 }} />
                </TableRow>
              ) : null}
              {virtualItems.map((virtualRow) => {
                const row = visibleRows[virtualRow.index];
                return (
                  <SeedSiteRow
                    key={row.id}
                    row={row}
                    isRunning={isRunning}
                    onUpdate={updateRow}
                  />
                );
              })}
              {paddingBottom > 0 ? (
                <TableRow style={{ height: paddingBottom }}>
                  <TableCell colSpan={6} sx={{ p: 0, border: 0 }} />
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
}

interface SeedSiteRowProps {
  row: Row;
  isRunning: boolean;
  onUpdate: (id: string, patch: Partial<Row>) => void;
}

const SeedSiteRow = memo(function SeedSiteRow({
  row,
  isRunning,
  onUpdate,
}: SeedSiteRowProps) {
  const urlValid = urlSchema.safeParse(row.url).success;
  const showUrlError =
    !row.isSeeded && row.selected && (row.url.trim().length === 0 || !urlValid);
  const disabled = row.isSeeded || isRunning;
  return (
    <TableRow
      hover
      selected={row.selected}
      sx={
        row.isSeeded
          ? {
              backgroundColor: "action.hover",
              "& > .MuiTableCell-root": {
                color: "text.secondary",
              },
            }
          : undefined
      }
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={row.selected}
          disabled={disabled}
          onChange={(e) => onUpdate(row.id, { selected: e.target.checked })}
        />
      </TableCell>
      <TableCell
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.name || "(unnamed)"}
      </TableCell>
      <TableCell sx={{ width: 96 }}>
        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
          {row.id}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: "45%" }}>
        <TextField
          fullWidth
          size="small"
          value={row.url}
          placeholder="https://example.com"
          error={showUrlError}
          helperText={
            showUrlError
              ? row.url.trim().length === 0
                ? "Website URL is required before seeding"
                : "Enter a valid URL"
              : " "
          }
          disabled={disabled}
          sx={{
            "& .MuiInputBase-input": {
              fontFamily: "monospace",
            },
          }}
          onChange={(e) => onUpdate(row.id, { url: e.target.value })}
        />
      </TableCell>
      <TableCell sx={{ width: 112 }}>
        {row.isSeeded ? (
          <Chip label="Seeded" color="success" size="small" />
        ) : (
          <Chip label="Missing" size="small" />
        )}
      </TableCell>
      <TableCell sx={{ width: 112 }}>
        <RowStatusCell status={row.status} />
      </TableCell>
    </TableRow>
  );
});

function RowStatusCell({ status }: { status: RowStatus }) {
  if (status.kind === "idle") {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  if (status.kind === "pending") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={14} />
        <Typography variant="body2">Seeding…</Typography>
      </Box>
    );
  }
  if (status.kind === "success") {
    return <Chip label="Success" color="success" size="small" />;
  }
  return (
    <Tooltip title={status.message} arrow>
      <Chip label="Error" color="error" size="small" />
    </Tooltip>
  );
}
